import {Trans, useLingui} from "@lingui/react/macro";
import {type ReactNode} from "react";
import type {NavigateFunction} from "react-router-dom";
import {useHref, useNavigate} from "react-router-dom";
import HumanTime from "../../HumanTime";
import type {
  AssetId,
  GroupEntry,
  GroupId,
  GroupRepo,
  RecipeEntry,
  ScheduledRecipeEntry,
  ScheduledRecipeId,
} from "../../models/GroupRepo";
import {useCookStats, useScheduledRecipeIds, useSortedRecipes} from "../../models/recipe-queries";
import {darkTheme} from "../../styles/themes/color-themes.css";
import {Box, Col, css, Text} from "../../ui/Box";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import groupByDate from "../../utils";
import {ShowAsset} from "../../utils/ShowAsset";
import useAnimationOnClick from "../../utils/use-animation-on-click";
import useLongPress from "../../utils/use-long-press";
import {useSuggestions} from "../items/AddItem";
import {MiniPill} from "../items/ItemPill";
import {orderRecipes, type RecipeHeading} from "./recipe-order";
import {isActiveSearch, type RecipeSearch} from "./recipe-search";
import {recipeStyles as styles} from "./recipes.css";
import {useRecipeIdsByItemId} from "./use-recipe-ids-by-item-id";
import {useRecipeView} from "./use-recipe-view";
import {addToPotlist, removeFromPotlist, usePotlist} from "./usePotItems";

type BaseRecipeTileProps = {
  children: ReactNode;
  navigate: NavigateFunction;
  recipe: RecipeEntry;
  query?: Record<string, any>;
  className?: string;
  groupRepo: GroupRepo;
  group: GroupEntry;
};

const BaseRecipeTile = (props: BaseRecipeTileProps) => {
  const {children, navigate, recipe, query, className, groupRepo, group} = props;
  const recipeId = recipe.id;
  const queryString = new URLSearchParams(query).toString();
  const baseUrl = queryString ? `./${recipeId}?${queryString}` : `./${recipeId}`;
  const clickHandlers = useLongPress({
    onLongClick: () => {
      navigate(`./${recipeId}/edit`);
    },
    onClick: () => {
      navigate(baseUrl);
    },
  });
  const href = useHref(baseUrl);
  return (
    <a
      {...clickHandlers}
      onClick={(e) => e.preventDefault()}
      href={href}
      className={css(
        {
          bg: "surface",
          rounded: "4",
          display: "flex",
          aspectRatio: "16 / 9",
          position: "relative",
          overflow: "hidden",
        },
        darkTheme,
        className
      )}
    >
      {recipe.coverImage && (
        <CoverImage assetId={recipe.coverImage} groupRepo={groupRepo} groupId={group.id} />
      )}
      <div className={styles.recipeTile.textBox}>
        <Text size="16" color="primary" bold>
          {recipe.name}
        </Text>
      </div>
      {children}
    </a>
  );
};

type CoverImageProps = {
  assetId: AssetId;
  groupRepo: GroupRepo;
  groupId: GroupId;
};
const CoverImage = ({assetId, groupRepo, groupId}: CoverImageProps) => {
  const asset = groupRepo.Assets.useById(assetId);
  if (!asset) return null;
  return (
    <ShowAsset
      asset={asset}
      groupId={groupId}
      className={css({
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        objectFit: "cover",
      })}
    />
  );
};

type RecipeTileProps = {
  recipe: RecipeEntry;
  navigate: NavigateFunction;
  inPotlist: boolean;
  group: GroupEntry;
  groupRepo: GroupRepo;
};
const RecipeTile = ({recipe, navigate, inPotlist, group, groupRepo}: RecipeTileProps) => {
  const handlePotlistClick = () => {
    if (inPotlist) {
      removeFromPotlist(group.id, recipe.id);
    } else {
      addToPotlist(group.id, recipe);
    }
  };
  return (
    <BaseRecipeTile
      recipe={recipe}
      groupRepo={groupRepo}
      group={group}
      navigate={navigate}
      className={inPotlist ? styles.recipeTile.selected : undefined}
    >
      <div className={styles.recipeTile.buttonBox}>
        <Button onClick={handlePotlistClick} variant="tertiary" size="sm" active={inPotlist}>
          <Icon name="pot-plus" />
        </Button>
      </div>
    </BaseRecipeTile>
  );
};

type ScheduleTileProps = {
  recipe: RecipeEntry;
  navigate: NavigateFunction;
  groupRepo: GroupRepo;
  element: ScheduledRecipeEntry;
  group: GroupEntry;
};

const ScheduleTile = ({recipe, navigate, groupRepo, element, group}: ScheduleTileProps) => {
  const {onClick, animClass} = useAnimationOnClick({
    onClick: () => groupRepo.ScheduledRecipes.patch(element.id, {completedAt: new Date()}),
  });

  return (
    <BaseRecipeTile
      recipe={recipe}
      groupRepo={groupRepo}
      group={group}
      navigate={navigate}
      query={{count: element.portionCount}}
      className={animClass ?? undefined}
    >
      <Box absolute top="0" right="0" pt="4" pr="4">
        <Button
          variant="primary"
          size="md"
          onClick={onClick}
          state={animClass ? "success" : undefined}
        >
          <Icon name="check" />
        </Button>
      </Box>
      <Box absolute bottom="0" right="0" pb="4" pr="4">
        <MiniPill>{element.portionCount}</MiniPill>
      </Box>
    </BaseRecipeTile>
  );
};

const ScheduleList = ({
  scheduledRecipeIds,
  groupRepo,
  group,
}: {
  scheduledRecipeIds: ScheduledRecipeId[];
  groupRepo: GroupRepo;
  group: GroupEntry;
}) => {
  const allRecipes = groupRepo.Recipes.useGetAll();
  const navigate = useNavigate();
  const scheduledRecipes = groupRepo.ScheduledRecipes.useAllByIds(scheduledRecipeIds);
  const groups = groupByDate({
    sortedItems: scheduledRecipes,
    extractDate: ({scheduledAt}) => scheduledAt,
  });
  return (
    <Col sp="16">
      <Text as="h2" size="20" color="primary">
        <Trans>Scheduled</Trans>
      </Text>
      {groups.map(({items: groupItems, newestDate}) => (
        <Col key={newestDate.getTime()} sp="8">
          <Text size="16" color="secondary">
            Scheduled <HumanTime date={newestDate} />
          </Text>
          <Box display="grid" sp="4" gridTemplateColumns="_itemPillGridCompact">
            {groupItems.map((element) => (
              <ScheduleTile
                key={element.id}
                navigate={navigate}
                recipe={allRecipes[element.recipeId]}
                element={element}
                groupRepo={groupRepo}
                group={group}
              />
            ))}
          </Box>
        </Col>
      ))}
    </Col>
  );
};

type RecipeGridProps = {
  recipes: RecipeEntry[];
  groupRepo: GroupRepo;
  group: GroupEntry;
};
const RecipeGrid = ({recipes, groupRepo, group}: RecipeGridProps) => {
  const navigate = useNavigate();
  const potlist = usePotlist(group.id) ?? {};
  return (
    <Box display="grid" sp="4" gridTemplateColumns="_itemPillGridCompact">
      {recipes.map((recipe) => (
        <RecipeTile
          key={recipe.id}
          recipe={recipe}
          navigate={navigate}
          inPotlist={Boolean(potlist[recipe.id])}
          group={group}
          groupRepo={groupRepo}
        />
      ))}
    </Box>
  );
};

const SectionHeading = ({heading}: {heading: RecipeHeading}) => {
  const {i18n} = useLingui();
  switch (heading.kind) {
    case "letter":
      return heading.letter;
    case "month":
      return new Intl.DateTimeFormat(i18n.locale, {month: "long", year: "numeric"}).format(
        new Date(heading.year, heading.month)
      );
    case "year":
      return heading.year;
    case "never":
      return <Trans>Never cooked</Trans>;
    case "count":
      return heading.min > 1 ? `${heading.min}+` : heading.min;
  }
};

type OrderedRecipesProps = RecipeGridProps & {
  emptyMessage: ReactNode;
  // keeps the given order, e.g. a search's best matches first
  unordered?: boolean;
};
/** The recipes in the order picked in `RecipeViewPicker`, with section headings unless compact. */
const OrderedRecipes = ({recipes, emptyMessage, unordered, ...rest}: OrderedRecipesProps) => {
  const {order, compact} = useRecipeView();
  const statsById = useCookStats(rest.groupRepo);
  const sections = unordered ? null : orderRecipes(recipes, order, statsById, new Date());
  if (recipes.length === 0) {
    return (
      <Text size="12" color="secondary">
        {emptyMessage}
      </Text>
    );
  }
  if (!sections) return <RecipeGrid recipes={recipes} {...rest} />;
  if (compact) return <RecipeGrid recipes={sections.flatMap((s) => s.recipes)} {...rest} />;
  return (
    <Col sp="16">
      {sections.map(({key, heading, recipes: sectionRecipes}) => (
        <Col key={key} sp="8">
          <Text size="16" color="secondary">
            <SectionHeading heading={heading} />
          </Text>
          <RecipeGrid recipes={sectionRecipes} {...rest} />
        </Col>
      ))}
    </Col>
  );
};

type SearchResultsProps = {
  search: RecipeSearch;
  allRecipes: RecipeEntry[];
  groupRepo: GroupRepo;
  group: GroupEntry;
};
const SearchResults = ({search, allRecipes, ...rest}: SearchResultsProps) => {
  const {query, itemIds} = search;
  const recipeIdsByItemId = useRecipeIdsByItemId(rest.groupRepo);
  const withItems =
    itemIds.length === 0
      ? allRecipes
      : allRecipes.filter((recipe) =>
          itemIds.every((itemId) => recipeIdsByItemId.get(itemId)?.has(recipe.id))
        );
  const matches = useSuggestions(query, withItems, (recipe) => recipe.name);
  const byRelevance = query.trim() !== "";
  return (
    <OrderedRecipes
      recipes={byRelevance ? matches : withItems}
      unordered={byRelevance}
      emptyMessage={<Trans>No matching recipes</Trans>}
      {...rest}
    />
  );
};

type RecipeListProps = {groupRepo: GroupRepo; group: GroupEntry; search: RecipeSearch | null};
const RecipeList = ({groupRepo, group, search}: RecipeListProps) => {
  const allRecipes = useSortedRecipes(groupRepo);
  const scheduledRecipeIds = useScheduledRecipeIds(groupRepo);

  return (
    <>
      {!isActiveSearch(search) && scheduledRecipeIds.length > 0 && (
        <ScheduleList scheduledRecipeIds={scheduledRecipeIds} groupRepo={groupRepo} group={group} />
      )}
      <Text as="h2" size="20" color="primary">
        <Trans>Recipes</Trans>
      </Text>
      {search && isActiveSearch(search) ? (
        <SearchResults
          search={search}
          allRecipes={allRecipes}
          groupRepo={groupRepo}
          group={group}
        />
      ) : (
        <OrderedRecipes
          recipes={allRecipes}
          emptyMessage={<Trans>No Recipes</Trans>}
          groupRepo={groupRepo}
          group={group}
        />
      )}
    </>
  );
};

export default RecipeList;
